// filepath: apps/frontend/src/stores/auth.ts
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import api from '@/services/api';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  twoFactorEnabled?: boolean;
  onboardedAt?: string | null;
  organization?: {
    id: string;
    name: string;
    slug: string;
  };
  savedSignature?: { image: string; mode: 'TYPED' | 'DRAWN' | 'UPLOADED' } | null;
}

interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  user: User;
}

interface TwoFactorChallenge {
  twoFactorRequired: true;
  challengeToken: string;
}

type LoginResult = AuthResponse | TwoFactorChallenge;

interface Session {
  id: string;
  userAgent: string | null;
  ipAddress: string | null;
  createdAt: string;
  lastUsedAt: string | null;
  expiresAt: string;
  current: boolean;
}

interface ApiEnvelope<T> {
  data: T;
  timestamp?: string;
}

const isTwoFactorChallenge = (result: LoginResult): result is TwoFactorChallenge =>
  (result as TwoFactorChallenge).twoFactorRequired === true;

export const useAuthStore = defineStore('auth', () => {
  // Tokens live in httpOnly cookies managed by the server — only the user is kept in memory.
  const user = ref<User | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const initialized = ref(false);

  const isAuthenticated = computed(() => !!user.value);
  const fullName = computed(() => (user.value ? `${user.value.firstName} ${user.value.lastName}` : ''));

  const login = async (email: string, password: string): Promise<LoginResult> => {
    loading.value = true;
    error.value = null;

    try {
      const response = await api.post<ApiEnvelope<LoginResult>>('/api/v1/auth/login', { email, password });
      const result = response.data.data;
      if (isTwoFactorChallenge(result)) {
        return result;
      }
      user.value = result.user;
      return result;
    } catch (err: any) {
      const payload = err.response?.data?.message;
      if (typeof payload === 'object' && payload?.code === 'EMAIL_NOT_VERIFIED') {
        error.value = payload.message || 'Please verify your email before signing in.';
      } else {
        error.value = typeof payload === 'string' ? payload : err.response?.data?.message || 'Login failed';
      }
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const verifyTwoFactor = async (challengeToken: string, code: string) => {
    loading.value = true;
    error.value = null;

    try {
      const response = await api.post<ApiEnvelope<AuthResponse>>('/api/v1/auth/2fa/login', {
        challengeToken,
        code,
      });
      user.value = response.data.data.user;
      return response.data.data;
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Two-factor verification failed';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const register = async (data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    organizationName?: string;
    acceptedTerms: boolean;
  }) => {
    loading.value = true;
    error.value = null;

    try {
      const response = await api.post<ApiEnvelope<{ message: string; email: string }>>(
        '/api/v1/auth/register',
        data,
      );
      return response.data.data;
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Registration failed';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const logout = async () => {
    try {
      await api.post('/api/v1/auth/logout', {});
    } catch {
      // Ignore logout errors — cookies are cleared server-side regardless
    } finally {
      user.value = null;
    }
  };

  const logoutAll = async () => {
    try {
      await api.post('/api/v1/auth/logout-all', {});
    } finally {
      user.value = null;
    }
  };

  const requestPasswordReset = async (email: string) => {
    loading.value = true;
    error.value = null;

    try {
      const response = await api.post<ApiEnvelope<{ message: string }>>('/api/v1/auth/password/forgot', { email });
      return response.data.data;
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Password reset request failed';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const resetPassword = async (token: string, newPassword: string) => {
    loading.value = true;
    error.value = null;

    try {
      const response = await api.post<ApiEnvelope<{ message: string }>>('/api/v1/auth/password/reset', {
        token,
        newPassword,
      });
      return response.data.data;
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Password reset failed';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const changePassword = async (currentPassword: string, newPassword: string) => {
    loading.value = true;
    error.value = null;

    try {
      const response = await api.post<ApiEnvelope<AuthResponse>>('/api/v1/auth/password/change', {
        currentPassword,
        newPassword,
      });
      user.value = response.data.data.user;
      return response.data.data;
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Password change failed';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const requestEmailChange = async (newEmail: string, currentPassword: string) => {
    loading.value = true;
    error.value = null;

    try {
      const response = await api.post<ApiEnvelope<{ message: string }>>('/api/v1/auth/email/change', {
        newEmail,
        currentPassword,
      });
      return response.data.data;
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Email change request failed';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const confirmEmailChange = async (token: string) => {
    const response = await api.post<ApiEnvelope<{ message: string }>>('/api/v1/auth/email/change/confirm', {
      token,
    });
    return response.data.data;
  };

  const saveSignature = async (payload: { mode: 'TYPED' | 'DRAWN' | 'UPLOADED'; image: string }) => {
    const response = await api.put<ApiEnvelope<{ image: string; mode: 'TYPED' | 'DRAWN' | 'UPLOADED' }>>(
      '/api/v1/users/me/signature',
      { signatureMode: payload.mode, signatureImage: payload.image },
    );
    if (user.value) user.value.savedSignature = response.data.data;
    return response.data.data;
  };

  const deleteSignature = async () => {
    await api.delete('/api/v1/users/me/signature');
    if (user.value) user.value.savedSignature = null;
  };

  const completeOnboarding = async () => {
    try {
      const response = await api.patch('/api/v1/users/me/onboarding');
      if (user.value) user.value.onboardedAt = response.data.data.onboardedAt;
    } catch {
      // Close locally anyway; the server flag is refreshed on next login.
      if (user.value) user.value.onboardedAt = new Date().toISOString();
    }
  };

  // --- Two-factor management -------------------------------------------------

  const setupTwoFactor = async () => {
    const response = await api.post<ApiEnvelope<{ otpauthUrl: string; qrCode: string; secret: string }>>(
      '/api/v1/auth/2fa/setup',
      {},
    );
    return response.data.data;
  };

  const enableTwoFactor = async (code: string) => {
    const response = await api.post<ApiEnvelope<{ message: string; backupCodes: string[] }>>(
      '/api/v1/auth/2fa/enable',
      { code },
    );
    return response.data.data;
  };

  const disableTwoFactor = async (payload: { code?: string; password?: string }) => {
    const response = await api.post<ApiEnvelope<{ message: string }>>('/api/v1/auth/2fa/disable', payload);
    return response.data.data;
  };

  // --- Session management ----------------------------------------------------

  const listSessions = async () => {
    const response = await api.get<ApiEnvelope<Session[]>>('/api/v1/auth/sessions');
    return response.data.data;
  };

  const revokeSession = async (id: string) => {
    const response = await api.delete<ApiEnvelope<{ message: string }>>(`/api/v1/auth/sessions/${id}`);
    return response.data.data;
  };

  // --- External login --------------------------------------------------------

  const externalLogin = async (
    provider: 'GOOGLE' | 'APPLE' | 'MICROSOFT' | 'SSO',
    data: { email?: string; providerId?: string; providerToken: string; firstName?: string; lastName?: string },
  ) => {
    loading.value = true;
    error.value = null;

    try {
      const endpoint = provider === 'SSO' ? '/api/v1/auth/sso' : '/api/v1/auth/oauth';
      const response = await api.post<ApiEnvelope<AuthResponse>>(endpoint, { provider, ...data });
      user.value = response.data.data.user;
      return response.data.data;
    } catch (err: any) {
      error.value = err.response?.data?.message || 'External login failed';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const getExternalLoginUrl = async (provider: 'GOOGLE' | 'APPLE' | 'MICROSOFT' | 'SSO') => {
    loading.value = true;
    error.value = null;

    try {
      const response = await api.get<ApiEnvelope<{ authorizationUrl: string }>>(
        `/api/v1/auth/oauth/${provider.toLowerCase()}/start`,
      );
      return response.data.data.authorizationUrl;
    } catch (err: any) {
      error.value = err.response?.data?.message || 'External login is not configured';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const exchangeOAuthCode = async (code: string) => {
    loading.value = true;
    error.value = null;

    try {
      await api.post<ApiEnvelope<AuthResponse>>('/api/v1/auth/oauth/exchange', { code });
      return fetchProfile();
    } catch (err: any) {
      error.value = err.response?.data?.message || 'External login failed';
      return false;
    } finally {
      loading.value = false;
    }
  };

  const refresh = async () => {
    try {
      await api.post('/api/v1/auth/refresh', {});
      return fetchProfile();
    } catch {
      user.value = null;
      return false;
    }
  };

  const fetchProfile = async () => {
    try {
      // skipAuthRefresh: this probe handles its own refresh below — avoid the
      // interceptor's redirect-to-login side effect on public pages.
      const response = await api.get<ApiEnvelope<User>>('/api/v1/auth/me', {
        skipAuthRefresh: true,
      } as any);
      user.value = response.data.data;
      return true;
    } catch {
      user.value = null;
      return false;
    }
  };

  const initAuth = async () => {
    if (initialized.value) return isAuthenticated.value;
    const loaded = await fetchProfile();
    if (!loaded) {
      // Access cookie may be expired while the refresh cookie is still valid
      await refresh();
    }
    initialized.value = true;
    return isAuthenticated.value;
  };

  return {
    user,
    loading,
    error,
    initialized,
    isAuthenticated,
    fullName,
    login,
    verifyTwoFactor,
    register,
    logout,
    logoutAll,
    requestPasswordReset,
    resetPassword,
    changePassword,
    requestEmailChange,
    confirmEmailChange,
    saveSignature,
    deleteSignature,
    completeOnboarding,
    setupTwoFactor,
    enableTwoFactor,
    disableTwoFactor,
    listSessions,
    revokeSession,
    externalLogin,
    getExternalLoginUrl,
    exchangeOAuthCode,
    refresh,
    fetchProfile,
    initAuth,
  };
});
